(() => {
  const VIEW_SELECTOR = '[data-hpf-viewport]';
  const STAGE_SELECTOR = '[data-hpf-stage]';
  const HOTSPOT_SELECTOR = '.hpf-hotspot[data-hpf-open-dialog]';
  const DOT_SELECTOR = '.hpf-hotspot__dot';
  const coarsePointer = window.matchMedia('(pointer: coarse)');

  const isVisible = (element) => {
    if (!(element instanceof HTMLElement)) return false;

    const style = getComputedStyle(element);
    return style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.pointerEvents !== 'none' &&
      element.getClientRects().length > 0;
  };

  const findNearestHotspot = (stage, clientX, clientY) => {
    let nearest = null;

    stage.querySelectorAll(HOTSPOT_SELECTOR).forEach((hotspot) => {
      if (!isVisible(hotspot)) return;

      const dot = hotspot.querySelector(DOT_SELECTOR) || hotspot;
      const rect = dot.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const centreX = rect.left + rect.width / 2;
      const centreY = rect.top + rect.height / 2;
      const distance = Math.hypot(clientX - centreX, clientY - centreY);

      if (!nearest || distance < nearest.distance) {
        nearest = { hotspot, rect, distance };
      }
    });

    return nearest;
  };

  const allowedDistance = (candidate) => {
    const visibleRadius = Math.max(candidate.rect.width, candidate.rect.height) / 2;
    const tolerance = coarsePointer.matches ? 6 : 8;
    const minimum = coarsePointer.matches ? 15 : 18;
    const maximum = coarsePointer.matches ? 24 : 46;

    return Math.min(maximum, Math.max(minimum, visibleRadius + tolerance));
  };

  const initView = (view) => {
    if (!(view instanceof HTMLElement) || view.dataset.hpfHotspotAccuracyReady === 'true') return;

    const stage = view.querySelector(STAGE_SELECTOR);
    if (!(stage instanceof HTMLElement)) return;

    view.dataset.hpfHotspotAccuracyReady = 'true';
    let rerouting = false;

    view.addEventListener('click', (event) => {
      if (rerouting || event.defaultPrevented) return;

      // Preserve keyboard activation and other synthetic clicks.
      if (event.detail === 0 && event.clientX === 0 && event.clientY === 0) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const clickedHotspot = target.closest(HOTSPOT_SELECTOR);
      const tappedStage = stage.contains(target);
      if (!clickedHotspot && !tappedStage) return;

      const candidate = findNearestHotspot(stage, event.clientX, event.clientY);

      if (clickedHotspot) {
        // A transformed touch target must stay close to its visible marker.
        if (!candidate || candidate.distance > allowedDistance(candidate)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        if (candidate.hotspot === clickedHotspot) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        rerouting = true;
        candidate.hotspot.click();
        rerouting = false;
        return;
      }

      // Keep only a modest screen-space tolerance around the visible dot so
      // empty diagram space remains easy to use for touch panning.
      if (!candidate || candidate.distance > allowedDistance(candidate)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      rerouting = true;
      candidate.hotspot.click();
      rerouting = false;
    }, true);
  };

  const scan = (scope = document) => {
    if (scope instanceof Element && scope.matches(VIEW_SELECTOR)) initView(scope);
    scope.querySelectorAll?.(VIEW_SELECTOR).forEach(initView);
  };

  const init = () => scan(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) scan(node);
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
